import { useState, useEffect, useMemo, forwardRef } from 'react';
import { useListContext, useNotify, useRefresh } from 'react-admin';
import authFetch from '../utils/authFetch';
import {
  Box, Card, CardContent, Typography, Stack, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, IconButton, Divider, CircularProgress,
} from '@mui/material';
import EditIcon    from '@mui/icons-material/Edit';
import DeleteIcon  from '@mui/icons-material/Delete';
import AddIcon     from '@mui/icons-material/Add';
import CloseIcon   from '@mui/icons-material/Close';
import SaveIcon    from '@mui/icons-material/Save';
import { btn, inputSx, NAVY, layout } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

// ── Edit Dialog ────────────────────────────────────────────────────────────────
const AttributeEditDialog = ({ attr, open, onClose, onSaved }) => {
  const notify = useNotify();
  const [label,    setLabel]    = useState('');
  const [attrKey,  setAttrKey]  = useState('');
  const [dataType, setDataType] = useState('select');
  const [required, setRequired] = useState(false);
  const [options,  setOptions]  = useState([]);
  const [newOpt,   setNewOpt]   = useState('');
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!attr) return;
    setLabel(attr.label || '');
    setAttrKey(attr.key || '');
    setDataType(attr.data_type || 'select');
    setRequired(!!attr.required);
    setOptions(Array.isArray(attr.options) ? [...attr.options] : []);
    setNewOpt('');
  }, [attr]);

  const addOption = () => {
    const val = newOpt.trim();
    if (!val) return;
    if (options.includes(val)) { notify('האפשרות כבר קיימת', { type: 'warning' }); return; }
    setOptions(o => [...o, val]);
    setNewOpt('');
  };

  const handleSave = async () => {
    if (!label.trim() || !attrKey.trim()) {
      notify('נא למלא תווית ומפתח', { type: 'warning' }); return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`/attributes/${attr.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: attrKey.trim(), label: label.trim(), data_type: dataType, required, options }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'שגיאה'); }
      notify('התכונה עודכנה', { type: 'success' });
      onSaved();
    } catch (e) { notify(e.message || 'שגיאה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        עריכת תכונה — {attr?.label || attr?.key}
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField label="תווית (label)" value={label} onChange={e => setLabel(e.target.value)} fullWidth sx={inputSx} />
          <TextField label="מפתח (key)" value={attrKey} onChange={e => setAttrKey(e.target.value)} fullWidth sx={inputSx} inputProps={{ dir: 'ltr', style: { fontFamily: 'monospace' } }} />
          <Box className="crm-form-grid" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>סוג נתון</InputLabel>
              <Select value={dataType} onChange={e => setDataType(e.target.value)} label="סוג נתון" sx={inputSx}>
                <MenuItem value="select">select — רשימה</MenuItem>
                <MenuItem value="text">text — טקסט חופשי</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={required} onChange={e => setRequired(e.target.checked)} />}
              label={required ? 'שדה חובה' : 'שדה אופציונלי'} sx={{ mr: 0, mt: 0.5 }}
            />
          </Box>
          <Divider />
          <Box>
            <Typography fontWeight={700} fontSize={14} color={NAVY} mb={1.5}>
              אפשרויות <Chip label={options.length} size="small" sx={{ ml: 1, height: 20, fontSize: 11 }} />
            </Typography>
            {options.length === 0
              ? <Typography fontSize={13} color="text.secondary" mb={1.5}>אין אפשרויות</Typography>
              : <Stack spacing={0.5} mb={2}>
                  {options.map((opt, i) => (
                    <Box key={opt} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: i % 2 === 0 ? 'rgba(13,29,62,0.03)' : '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 1.5, px: 2, py: 0.8 }}>
                      <Typography fontSize={13} sx={{ fontFamily: 'monospace' }}>{opt}</Typography>
                      <IconButton size="small" onClick={() => setOptions(o => o.filter(x => x !== opt))} aria-label={`הסרת אפשרות ${opt}`} sx={{ color: '#c62828' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
            }
            <Stack className="crm-filter-row" direction="row" spacing={1}>
              <TextField size="small" placeholder="אפשרות חדשה..." value={newOpt}
                onChange={e => setNewOpt(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addOption()}
                sx={{ ...inputSx, flex: 1 }} inputProps={{ dir: 'ltr', style: { fontFamily: 'monospace' } }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addOption}
                disabled={!newOpt.trim()} sx={{ ...btn.primary, height: 40 }}>הוסף</Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Button onClick={onClose} sx={{ color: '#666', borderRadius: 2 }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} sx={btn.primary}>
          שמור שינויים
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Attribute Card ─────────────────────────────────────────────────────────────
const DATA_TYPE_LABELS = { select: 'רשימה', text: 'טקסט' };

const AttributeCard = forwardRef(({ attr, onEdit }, ref) => (
  <Card ref={ref} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', height: '100%' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography fontWeight={700} fontSize={15} color={NAVY}>{attr.label || attr.key}</Typography>
        {attr.required && <Chip label="חובה" size="small" sx={{ backgroundColor: '#FFF3CD', color: '#856404', fontSize: 10, height: 18 }} />}
      </Stack>
      <Stack className="crm-wrap" direction="row" spacing={0.8} mb={1.5} flexWrap="wrap">
        <Chip label={`key: ${attr.key}`} size="small" sx={{ backgroundColor: 'rgba(13,29,62,0.07)', fontSize: 10, fontFamily: 'monospace' }} />
        <Chip label={DATA_TYPE_LABELS[attr.data_type] || attr.data_type} size="small" sx={{ backgroundColor: 'rgba(200,169,110,0.15)', color: '#7a6030', fontSize: 10 }} />
      </Stack>
      {/* Capped height — an attribute with a very long options list would
          otherwise make this one card towers over its neighbors. */}
      <Box sx={{ flex: 1, maxHeight: '92px', mb: 2, overflow: 'hidden' }}>
        {(!attr.options || attr.options.length === 0)
          ? <Typography fontSize={12} color="text.secondary">אין אפשרויות</Typography>
          : <Stack spacing={0.3}>
              {attr.options.slice(0, 4).map(opt => <Typography key={opt} fontSize={12} color="text.secondary">• {opt}</Typography>)}
              {attr.options.length > 4 && (
                <Typography fontSize={11} color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {`+${attr.options.length - 4} נוספות`}
                </Typography>
              )}
            </Stack>
        }
      </Box>
      <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />}
        onClick={() => onEdit(attr)} fullWidth
        sx={{ borderColor: NAVY, color: NAVY, borderRadius: 2, fontSize: 12, '&:hover': { backgroundColor: 'rgba(13,29,62,0.06)' } }}>
        ערוך תכונה
      </Button>
    </CardContent>
  </Card>
));

// ── Main ───────────────────────────────────────────────────────────────────────
const AttributeCards = () => {
  const { data = [], isLoading } = useListContext();
  const refresh = useRefresh();
  const [editingAttr, setEditingAttr] = useState(null);
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 12, page });

  const pageItems = useMemo(() => {
    return data.slice(startIndex, startIndex + renderCount);
  }, [data, startIndex, renderCount]);

  if (isLoading) return <Box className="crm-page" p={3}><Typography>טוען...</Typography></Box>;
  if (!data.length) return <Box p={4} textAlign="center"><Typography color="text.secondary">אין תכונות עדיין</Typography></Box>;

  return (
    <>
      <Box className="crm-card-grid" ref={containerRef} sx={{ ...layout.cardGrid, minHeight: 200 }}>
        {pageItems.map((attr) => (
          <AttributeCard key={attr.id} attr={attr} onEdit={setEditingAttr} />
        ))}
      </Box>
      <FitPagination page={page} setPage={setPage} total={data.length} perPage={fitCount} />
      <AttributeEditDialog
        key={editingAttr?.id ?? 'none'}
        attr={editingAttr}
        open={!!editingAttr}
        onClose={() => setEditingAttr(null)}
        onSaved={() => { setEditingAttr(null); refresh(); }}
      />
    </>
  );
};

export default AttributeCards;
